import React, { useState, useEffect, useMemo } from 'react';
import './style.css';
import { IoIosSearch } from 'react-icons/io';
import { BsGrid3X3Gap, BsListUl } from 'react-icons/bs';
import { FiChevronDown } from 'react-icons/fi';

const patientData = [
  {
    id: '5fyD0E',
    name: 'JOHN HENRY DOE',
    birthDate: 'Tuesday, March 1, 1990',
    sex: 'M',
    xrayImages: [
      '/path-to-xray-1.jpg',
      '/path-to-xray-2.jpg'
    ]
  },
  {
    id: '6gzF1G',
    name: 'JANE MARIE SMITH',
    birthDate: 'Friday, April 15, 1985',
    sex: 'F',
    xrayImages: [
      '/path-to-xray-1.jpg',
      '/path-to-xray-2.jpg'
    ]
  },
  {
    id: '7hxK2H',
    name: 'ROBERT JAMES JOHNSON',
    birthDate: 'Monday, July 10, 1978',
    sex: 'M',
    xrayImages: [
      '/path-to-xray-1.jpg',
      '/path-to-xray-2.jpg'
    ]
  },
  {
    id: '8jyL3J',
    name: 'SARAH ELIZABETH WILLIAMS',
    birthDate: 'Wednesday, September 22, 1992',
    sex: 'F',
    xrayImages: [
      '/path-to-xray-1.jpg',
      '/path-to-xray-2.jpg'
    ]
  },
  {
    id: '9kzM4K',
    name: 'MICHAEL DAVID BROWN',
    birthDate: 'Saturday, November 5, 1980',
    sex: 'M',
    xrayImages: [
      '/path-to-xray-1.jpg',
      '/path-to-xray-2.jpg'
    ]
  },
  {
    id: '10lAN5L',
    name: 'EMILY NICOLE DAVIS',
    birthDate: 'Thursday, February 14, 1995',
    sex: 'F',
    xrayImages: [
      '/path-to-xray-1.jpg',
      '/path-to-xray-2.jpg'
    ]
  }
];

// Placeholder images for now
const placeholderXRayImages = [
  'https://medlineplus.gov/images/Xray_share.jpg',
  'https://static.wixstatic.com/media/ebdd4d_df3141d0c1394a068d5e13c7d3d73ff2~mv2.jpg/v1/fill/w_568,h_380,al_c,lg_1,q_80,enc_avif,quality_auto/ebdd4d_df3141d0c1394a068d5e13c7d3d73ff2~mv2.jpg',
  'https://www.ucsfbenioffchildrens.org/-/media/project/ucsf/ucsf-bch/images/medical-tests/hero/pelvis-x-ray-708x556-2x.jpg?rev=1c064a964373497895854bd8d7e41aa7',
  'https://horizonnb.ca/wp-content/uploads/2023/11/X-ray.jpg'
];

const PatientCard = ({ patient }) => {
  return (
    <div className="patient-card">
      <div className="patient-card__images">
        <div className="patient-card__image-grid">
          {placeholderXRayImages.map((image, index) => (
            <div key={index} className="patient-card__image-container">
              <img src={image} alt={`X-Ray ${index + 1}`} className="patient-card__image" />
            </div>
          ))}
        </div>
      </div>
      <div className="patient-card__info">
        <h3 className="patient-card__name">{patient.name}</h3>
        <p className="patient-card__detail">Patient ID: {patient.id}</p>
        <p className="patient-card__detail">Birth Date: {patient.birthDate}</p>
        <p className="patient-card__detail">Sex: {patient.sex}</p>
      </div>
    </div>
  );
};

function AllPatient() {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');

  // Use matchMedia instead of manually tracking window width
  useEffect(() => {
    // Create media query for mobile screens
    const mobileMediaQuery = window.matchMedia('(max-width: 37.5rem)'); // 600px in rem
    
    // Function to handle media query changes
    const handleMediaQueryChange = (event) => {
      if (event.matches && viewMode === 'grid') {
        // If screen is now small and we're in grid view, switch to list view
        setViewMode('list');
      }
    };
    
    // Check initial state
    if (mobileMediaQuery.matches && viewMode === 'grid') {
      setViewMode('list');
    }
    
    // Add listener for changes
    mobileMediaQuery.addEventListener('change', handleMediaQueryChange);
    
    // Clean up
    return () => {
      mobileMediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, [viewMode]);

  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  // Filter patients based on search term
  const filteredPatients = useMemo(() => {
    if (!searchTerm.trim()) {
      return patientData;
    }
    
    const lowercasedSearchTerm = searchTerm.toLowerCase().trim();
    
    return patientData.filter(patient => {
      const nameMatch = patient.name.toLowerCase().includes(lowercasedSearchTerm);
      const idMatch = patient.id.toLowerCase().includes(lowercasedSearchTerm);
      
      return nameMatch || idMatch;
    });
  }, [searchTerm]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="all-patients">
      <div className="all-patients__header">
        <div className="all-patients__search-container">
          <div className="all-patients__search-input-wrapper">
            <IoIosSearch className="all-patients__search-icon" />
            <input 
              type="text" 
              placeholder="Search by name or ID" 
              className="all-patients__search-input"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button 
                className="all-patients__search-clear" 
                onClick={clearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="all-patients__filters">
          <button className="all-patients__filter-btn">
            <span>Last Updated</span>
            <FiChevronDown className="all-patients__filter-icon" />
          </button>
          <button className="all-patients__filter-btn">
            <span>All Institutions</span>
            <FiChevronDown className="all-patients__filter-icon" />
          </button>
          <button 
            className="all-patients__grid-btn"
            onClick={toggleViewMode}
            aria-label={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
          >
            {viewMode === 'grid' ? <BsListUl /> : <BsGrid3X3Gap />}
          </button>
        </div>
      </div>
      
      <div className={`all-patients__grid ${viewMode === 'list' ? 'all-patients__grid--list' : ''}`}>
        {filteredPatients.length > 0 ? (
          filteredPatients.map((patient, index) => (
            <PatientCard key={index} patient={patient} />
          ))
        ) : (
          <div className="all-patients__no-results">
            <p>No patients found matching "{searchTerm}"</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllPatient;